import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, CalendarClock, ArrowRightLeft, XCircle, ShieldCheck, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Seo } from "../components/Seo";
import { http } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PRICE_LABEL = { monthly: "A$6.99 / month", annual: "A$69 / year" };
const CYCLE_NAME = { monthly: "Monthly", annual: "Annual" };

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d.length <= 10 ? `${d}T00:00:00` : d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return d; }
};

export default function Account() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);

  const load = useCallback(async () => {
    setFetching(true);
    try { const { data } = await http.get("/billing/subscription"); setSub(data); }
    catch { setSub(null); }
    finally { setFetching(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { state: { from: "/account" } }); return; }
    load();
  }, [user, loading, navigate, load]);

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/billing/subscription/cancel");
      toast.success(`Subscription cancelled — access continues until ${fmtDate(data.ends_at)}.`);
      setCancelOpen(false);
      await load(); await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel. Please try again.");
    } finally { setBusy(false); }
  };

  const doSwap = async () => {
    const target = sub.cycle === "annual" ? "monthly" : "annual";
    setBusy(true);
    try {
      await http.post("/billing/subscription/swap", { cycle: target });
      toast.success(`You'll switch to ${CYCLE_NAME[target]} billing at your next renewal.`);
      setSwapOpen(false);
      await load(); await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch plans. Please try again.");
    } finally { setBusy(false); }
  };

  const otherCycle = sub?.cycle === "annual" ? "monthly" : "annual";

  return (
    <>
      <Seo title="Manage subscription" path="/account" description="Manage your Framer's Bench subscription — view your plan, switch between monthly and annual, or cancel." />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6 lg:px-8">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Your subscription</h1>
        <p className="mt-2 text-sm text-muted-foreground">Manage your plan, billing cycle and cancellation.</p>

        {fetching ? (
          <div className="mt-10 flex items-center justify-center py-16 text-muted-foreground" data-testid="account-loading">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading your subscription…
          </div>
        ) : !sub?.subscribed ? (
          <div className="mt-8 rounded-2xl border border-border bg-card p-8 text-center" data-testid="account-not-subscribed">
            <Sparkles className="mx-auto h-8 w-8 text-primary" />
            <h2 className="mt-4 font-heading text-xl font-bold">You're not subscribed yet</h2>
            <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
              {sub?.access === "trial"
                ? "You're on your free trial. Subscribe to keep your projects, unlock the materials cost calculator and export cut sheets."
                : sub?.access === "expired"
                ? "Your free trial has ended. Subscribe to restore saving, exporting and the materials cost calculator."
                : "Subscribe to unlock permanent saves, the materials cost calculator and more."}
            </p>
            <Button className="mt-5" onClick={() => navigate("/pricing")} data-testid="account-see-plans">See plans</Button>
          </div>
        ) : (
          <div className="mt-8 space-y-5">
            {/* Plan card */}
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="account-plan-card">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-primary" />
                    <h2 className="font-heading text-lg font-bold" data-testid="account-plan-name">Framer's Bench · {CYCLE_NAME[sub.cycle] || "Subscription"}</h2>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground" data-testid="account-plan-price">{PRICE_LABEL[sub.cycle] || "Active subscription"}</p>
                </div>
                <Badge data-testid="account-status-badge">{sub.pending_cancel ? "Cancelling" : "Active"}</Badge>
              </div>

              <div className="mt-5 space-y-2.5 border-t border-border pt-5 text-sm">
                {sub.pending_cancel ? (
                  <div className="flex items-center gap-2 text-amber-700" data-testid="account-cancel-notice">
                    <CalendarClock className="h-4 w-4 shrink-0" />
                    <span>Your subscription ends on <strong>{fmtDate(sub.canceled_date || sub.pending_effective_date)}</strong>. You keep full access until then.</span>
                  </div>
                ) : sub.renewal_date ? (
                  <div className="flex items-center gap-2 text-muted-foreground" data-testid="account-renewal">
                    <CalendarClock className="h-4 w-4 shrink-0" />
                    <span>Renews automatically on <strong className="text-foreground">{fmtDate(sub.renewal_date)}</strong>.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <ShieldCheck className="h-4 w-4 shrink-0" /> <span>Your subscription is active.</span>
                  </div>
                )}

                {sub.pending_swap_cycle && (
                  <div className="flex items-center gap-2 text-primary" data-testid="account-swap-notice">
                    <ArrowRightLeft className="h-4 w-4 shrink-0" />
                    <span>Switching to <strong>{CYCLE_NAME[sub.pending_swap_cycle]}</strong> billing at your next renewal.</span>
                  </div>
                )}
              </div>
            </div>

            {/* Manage actions */}
            {sub.manageable ? (
              <div className="rounded-2xl border border-border bg-card p-6" data-testid="account-manage">
                <h3 className="font-heading text-base font-bold">Manage plan</h3>
                <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                  {!sub.pending_cancel && !sub.pending_swap_cycle && (
                    <Button variant="outline" className="flex-1" onClick={() => setSwapOpen(true)} disabled={busy} data-testid="account-switch-btn">
                      <ArrowRightLeft className="mr-2 h-4 w-4" /> Switch to {CYCLE_NAME[otherCycle]} ({PRICE_LABEL[otherCycle]})
                    </Button>
                  )}
                  {!sub.pending_cancel && (
                    <Button variant="outline" className="flex-1 text-destructive hover:text-destructive" onClick={() => setCancelOpen(true)} disabled={busy} data-testid="account-cancel-btn">
                      <XCircle className="mr-2 h-4 w-4" /> Cancel subscription
                    </Button>
                  )}
                  {sub.pending_cancel && (
                    <Button className="flex-1" onClick={() => navigate("/pricing")} data-testid="account-resubscribe-btn">
                      Re-subscribe
                    </Button>
                  )}
                </div>
                <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5" /> Billing secured by Square. Changes take effect at your next renewal.
                </p>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-6 text-sm text-muted-foreground" data-testid="account-unmanageable">
                Your subscription is active. To change or cancel it, please contact support.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cancel confirm */}
      <Dialog open={cancelOpen} onOpenChange={(o) => !busy && setCancelOpen(o)}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel your subscription?</DialogTitle>
            <DialogDescription>
              You'll keep full access until the end of your current billing period{sub?.renewal_date ? ` (${fmtDate(sub.renewal_date)})` : ""}. After that you'll return to the free calculator and your saved projects will be removed. You can re-subscribe any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setCancelOpen(false)} disabled={busy} data-testid="cancel-keep">Keep my subscription</Button>
            <Button variant="destructive" onClick={doCancel} disabled={busy} data-testid="cancel-confirm">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Cancel at period end
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Swap confirm */}
      <Dialog open={swapOpen} onOpenChange={(o) => !busy && setSwapOpen(o)}>
        <DialogContent data-testid="swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Switch to {CYCLE_NAME[otherCycle]} billing?</DialogTitle>
            <DialogDescription>
              Your plan will change to <strong>{PRICE_LABEL[otherCycle]}</strong> at your next renewal{sub?.renewal_date ? ` on ${fmtDate(sub.renewal_date)}` : ""}. No charge today, and you keep your current plan until then.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setSwapOpen(false)} disabled={busy} data-testid="swap-cancel">Not now</Button>
            <Button onClick={doSwap} disabled={busy} data-testid="swap-confirm">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm switch
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
